import { useLayoutEffect, useMemo } from "react";
import { useUserpilot } from "@platform/hooks/useUserpilot";

import RefreshIcon from "@assets/icons/refresh.svg";
import useDialogStore from "@store/useDialogStore";
import { useAIContext } from "./hooks/useAIContext";
import { useContextFeedback } from "./hooks/useContextFeedback";
import { useTransformAIContext } from "./hooks/useTransformAIContext";
import { ERROR_STRINGS } from "@config/messages";
import { getRelativeTime } from "@utils/date.util";
import { IThreadResponse } from "@interfaces/thread.interface";
import { AIContextHeader } from "./AIContextHeader";
import { AIContextFooter } from "./AIContextFooter";
import { AIContextSkeleton } from "./AIContextSkeleton";
import { FeedbackDialog } from "./FeedbackDialog";
import { ThankYouDialog } from "./ThankYouDialog";
import { GenerationStatus } from "./GenerationStatus";
import { MarkdownContent } from "./MarkdownContent";
import { View } from "@ui/View";

interface Props {
  thread: IThreadResponse;
}

export function AIContextSection({ thread }: Props) {
  const { track } = useUserpilot();
  const { openDialog } = useDialogStore();
  const {
    data: context,
    refetchContext,
    regenerateContext,
    isLoading,
    isPlaceholderData,
  } = useAIContext(thread.id);
  const { submitThumbsUp, submitThumbsDown, submitReasons, isSubmitting } = useContextFeedback(
    thread.id,
    context?.feedback
  );
  const { lines, citationsSources, orderedCitations } = useTransformAIContext(
    context?.context,
    context?.sources
  );

  const isPending = context?.status === "pending" || context?.status === "in_progress";
  const isSuccess = context?.success && context?.status !== "failed";
  const isError = !isSuccess && !isLoading;
  const hasFeedback = Boolean(context?.feedback);
  const hasReasons = context?.feedback?.reasons?.length > 0;
  const showFeedback = isSuccess && !isPending && !isPlaceholderData;
  // Hidden while a rating is in flight: the cached rating is stale until it resolves.
  const showFeedbackDialogTrigger =
    hasFeedback && !hasReasons && context?.feedback?.rating !== "thumbs_up" && !isSubmitting;

  const lastEdited = useMemo(() => {
    return context?.generated_at ? getRelativeTime(context.generated_at) : "";
  }, [context?.generated_at]);

  const message = isError ? ERROR_STRINGS.UPDATE_AI_CONTEXT : lastEdited;

  const handleRegenerateContext = () => {
    regenerateContext();
    track("Click: Regenerate AI context");
  };

  const handleThumbsUp = () => {
    submitThumbsUp();
    track("Click: AI context thumbs up");
  };

  const handleThumbsDown = () => {
    submitThumbsDown();
    track("Click: AI context thumbs down");
  };

  const handleSubmitReasons = () => {
    openDialog(
      <FeedbackDialog
        onSubmit={async (reasons, comment) => {
          try {
            await submitReasons(reasons, comment);
          } catch {
            return; // the mutation's onError already surfaced the toast
          }

          openDialog(<ThankYouDialog />);
          track("Click: AI context thumbs down feedback submitted");
        }}
      />
    );
  };

  useLayoutEffect(() => {
    if (!context) return;

    // Only refetch while the status is "pending" or "in_progress"
    if (isPending) {
      const intervalId = setInterval(() => {
        refetchContext();
      }, 1500);

      return () => clearInterval(intervalId);
    }
  }, [context]);

  return (
    <div className="flex flex-col gap-2 border dark:border-gray-700 dark:bg-gray-850 rounded-lg py-2 px-3">
      <AIContextHeader
        isPending={!!isPending}
        isError={isError}
        onRegenerate={handleRegenerateContext}
      />

      {/* Generated context */}
      <View.Condition if={!isPending}>
        <div className="dark:text-invertedPrimary80 animate-fade-in text-base">
          {lines.map((line) => (
            <MarkdownContent
              key={line}
              text={line}
              sources={citationsSources}
              order={orderedCitations}
            />
          ))}
        </div>

        <AIContextFooter
          message={message}
          isError={isError}
          showFeedback={showFeedback}
          showFeedbackDialogTrigger={showFeedbackDialogTrigger}
          feedbackRating={context?.feedback?.rating}
          isSubmitting={isSubmitting}
          onThumbsUp={handleThumbsUp}
          onThumbsDown={handleThumbsDown}
          onSubmitReasons={handleSubmitReasons}
        />
      </View.Condition>

      {/* First loading or generation process */}
      <View.Condition if={isPending}>
        <div className="dark:text-invertedPrimary80 animate-fade-in">
          {lines.length === 0 ? (
            <AIContextSkeleton className="pb-2" />
          ) : (
            <div className="select-text opacity-50 text-base">
              {lines.map((line) => (
                <MarkdownContent
                  key={line}
                  text={line}
                  sources={citationsSources}
                  order={orderedCitations}
                />
              ))}
            </div>
          )}
        </div>

        <div className="w-full border-t dark:border-gray-600 pt-2">
          <div className="flex items-center gap-4">
            <div className="animate-slowspin">
              <RefreshIcon className="text-sky-600 scale-125" />
            </div>
            <GenerationStatus />
          </div>
        </div>
      </View.Condition>
    </div>
  );
}
